import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaSearch, FaStar, FaGraduationCap, FaTimes, FaFilter } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { topStudentsAPI } from "../services/postgresService";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ParallaxHeroSection from "../components/ParallaxHeroSection";
import { createPlaceholderDataUrl, handleImageError } from "../utils/imageUtils";

const TopStudentCard = ({ student, index, onViewMore, t }) => {
  const getAchievementTypeLabel = (type) => {
    const labels = {
      academic: t('topStudents.academicExcellence'),
      leadership: t('topStudents.leadership'),
      improvement: t('topStudents.mostImproved'),
      special: t('topStudents.specialRecognition'),
    };
    return labels[type] || type;
  };

  const getAchievementBadgeColor = (type) => {
    switch (type) {
      case 'academic': return 'bg-blue-100 text-blue-800';
      case 'leadership': return 'bg-green-100 text-green-800';
      case 'improvement': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-purple-100 text-purple-800';
    }
  };

  return (
    <motion.div
      className="bg-white rounded-lg shadow-lg overflow-hidden h-full flex flex-col"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{
        y: -10,
        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
      }}
    >
      <div className="relative h-64 overflow-hidden">
        <img
          src={student.photo || createPlaceholderDataUrl(400, 300, student.name || t('labels.name'))}
          alt={student.name}
          className="w-full h-full object-cover"
          onError={(e) => handleImageError(e, 400, 300, student.name)}
        />
        {student.is_featured && (
          <div className="absolute top-4 right-4 bg-yellow-500 text-white p-2 rounded-full">
            <FaStar className="text-lg" />
          </div>
        )}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
          <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getAchievementBadgeColor(student.achievement_type)}`}>
            {getAchievementTypeLabel(student.achievement_type)}
          </span>
        </div>
      </div>

      <div className="p-6 flex-grow flex flex-col">
        <h3 className="text-xl font-bold text-safir-dark mb-1">
          {student.name}
        </h3>
        <p className="text-safir-primary font-medium text-sm mb-3">
          {student.achievement_title || t('topStudents.achievement')}
        </p>

        {student.award_date && (
          <div className="flex items-center text-gray-500 text-sm mb-3">
            <FaGraduationCap className="mr-2 text-safir-primary" />
            <span>{new Date(student.award_date).toLocaleDateString(undefined, { year: 'numeric', month: 'long' })}</span>
          </div>
        )}

        <div className="flex-grow">
          <p className="text-gray-600 text-sm line-clamp-2">
            {student.preview_text || (student.story ? student.story.substring(0, 150) + "..." : "")}
          </p>
        </div>

        <button
          onClick={() => onViewMore(student)}
          className="mt-4 text-safir-primary font-medium text-sm hover:underline focus:outline-none focus:ring-2 focus:ring-safir-primary focus:ring-offset-2 rounded"
        >
          {t('topStudents.viewMore')}
        </button>
      </div>
    </motion.div>
  );
};

const StudentModal = ({ student, onClose, t }) => {
  if (!student) return null;

  const getAchievementTypeLabel = (type) => {
    const labels = {
      academic: t('topStudents.academicExcellence'),
      leadership: t('topStudents.leadership'),
      improvement: t('topStudents.mostImproved'),
      special: t('topStudents.specialRecognition'),
    };
    return labels[type] || type;
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="relative h-64 md:h-80">
            <img
              src={student.photo || createPlaceholderDataUrl(800, 400, student.name || t('labels.name'))}
              alt={student.name}
              className="w-full h-full object-cover"
              onError={(e) => handleImageError(e, 800, 400, student.name)}
            />
            <button
              onClick={onClose}
              className="absolute top-4 right-4 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-colors"
            >
              <FaTimes className="text-gray-600" />
            </button>
            {student.is_featured && (
              <div className="absolute top-4 left-4 bg-yellow-500 text-white px-3 py-1 rounded-full flex items-center">
                <FaStar className="mr-1" /> {t('topStudents.featured')}
              </div>
            )}
          </div>

          <div className="p-6 md:p-8">
            <div className="flex flex-wrap gap-2 mb-4">
              <span className="px-3 py-1 bg-safir-primary/10 text-safir-primary rounded-full text-sm font-medium">
                {getAchievementTypeLabel(student.achievement_type)}
              </span>
              {student.course_name && (
                <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm">
                  {student.course_name}
                </span>
              )}
            </div>

            <h2 className="text-2xl md:text-3xl font-bold text-safir-dark mb-2">
              {student.name}
            </h2>
            
            <p className="text-safir-primary font-medium text-lg mb-4">
              {student.achievement_title || t('topStudents.achievement')}
            </p>

            {student.award_date && (
              <div className="flex items-center text-gray-500 text-sm mb-6">
                <FaGraduationCap className="mr-2 text-safir-primary" />
                <span>{t('topStudents.recognizedOn')} {new Date(student.award_date).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              </div>
            )}

            <div className="prose prose-safir max-w-none">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">{t('topStudents.successStory')}</h3>
              <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">
                {student.story}
              </p>
            </div>

            <div className="mt-8 pt-6 border-t">
              <button
                onClick={onClose}
                className="w-full md:w-auto bg-safir-primary hover:bg-safir-secondary text-white px-6 py-3 rounded-lg transition-colors"
              >
                {t('topStudents.close')}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const TopStudentsPage = () => {
  const { t, i18n } = useTranslation();
  const [topStudents, setTopStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState("all");
  const [selectedStudent, setSelectedStudent] = useState(null);

  const achievementTypes = [
    { value: "all", label: t('topStudents.allAchievements') },
    { value: "academic", label: t('topStudents.academicExcellence') },
    { value: "leadership", label: t('topStudents.leadership') },
    { value: "improvement", label: t('topStudents.mostImproved') },
    { value: "special", label: t('topStudents.specialRecognition') },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    const fetchTopStudents = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await topStudentsAPI.getAll();
        setTopStudents(data || []);
      } catch (err) {
        console.error("Error fetching top students:", err);
        setError(t('topStudents.error'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchTopStudents();
  }, [t]);

  const filteredStudents = useMemo(() => {
    return topStudents.filter((student) => {
      const matchesSearch =
        student.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.achievement_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.story?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesType =
        selectedType === "all" || student.achievement_type === selectedType;

      return matchesSearch && matchesType;
    });
  }, [topStudents, searchTerm, selectedType]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleTypeChange = (e) => {
    setSelectedType(e.target.value);
  };

  const handleViewMore = (student) => {
    setSelectedStudent(student);
  };

  const handleCloseModal = () => {
    setSelectedStudent(null);
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 py-20 text-center">
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
            <p className="font-bold">{t('common.error')}</p>
            <p>{error}</p>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="bg-safir-primary text-white px-4 py-2 rounded hover:bg-safir-dark transition-colors"
          >
            {t('topStudents.tryAgain')}
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Hero Section */}
      <ParallaxHeroSection
        backgroundKey="students"
        title={t('topStudents.title')}
        subtitle={t('topStudents.subtitle')}
      >
        <motion.div
          className="w-24 h-1 bg-safir-primary mx-auto mt-4"
          initial={{ width: 0 }}
          animate={{ width: 96 }}
          transition={{ duration: 0.8 }}
        ></motion.div>
      </ParallaxHeroSection>

      {/* Search and Filter Section */}
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-md p-6 mb-8 -mt-16 max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaSearch className="text-gray-400" />
              </div>
              <input
                type="text"
                placeholder={t('topStudents.searchPlaceholder')}
                value={searchTerm}
                onChange={handleSearchChange}
                className="pl-10 w-full p-3 border border-gray-300 rounded-md focus:ring-safir-primary focus:border-safir-primary"
              />
            </div>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaFilter className="text-gray-400" />
              </div>
              <select
                value={selectedType}
                onChange={handleTypeChange}
                className="pl-10 w-full p-3 border border-gray-300 rounded-md focus:ring-safir-primary focus:border-safir-primary appearance-none bg-white"
              >
                {achievementTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Students Grid */}
      <div className="container mx-auto px-4 py-8">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-safir-primary mx-auto"></div>
            <p className="mt-4 text-gray-600">{t('topStudents.loading')}</p>
          </div>
        ) : filteredStudents.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-xl text-gray-600">
              {t('topStudents.noResults')}
            </p>
          </div>
        ) : (
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {filteredStudents.map((student, index) => (
              <TopStudentCard
                key={student.id}
                student={student}
                index={index}
                onViewMore={handleViewMore}
                t={t}
              />
            ))}
          </motion.div>
        )}
      </div>

      <Footer />

      {/* Modal */}
      {selectedStudent && (
        <StudentModal student={selectedStudent} onClose={handleCloseModal} t={t} />
      )}
    </div>
  );
};

export default TopStudentsPage;
